import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, Button, Paper, TextField, Typography } from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import trLocale from 'date-fns/locale/tr';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useNavigate } from 'react-router-dom';
import { PaginatedResponse } from '../../interfaces/PaginatedResponse';
import { TrendyolOrder } from '../../interfaces/Trendyol/TrendyolOrder';
import {
  getDefaultPlatformOrderFilter,
  PlatformOrderFilter,
} from '../../interfaces/PlatformOrder/PlatformOrderFilter';
import { getTrendyolOrders } from '../../api/Trendyol';
import { Pagination } from '../../interfaces/Pagination';
import { sharedStyles } from '../../utils/Style';
import { Waiting } from '../../components/Waiting/Waiting';
import { TrendyolOrdersTable } from './TrendyolOrdersTable/TrendyolOrdersTable';
import { OrderFilter } from '../../interfaces/Order/OrderFilter';
import { FilterSearchBox } from '../../components/SearchBox/FilterSearchBox';

const dateTimePickerStyle = {
  minWidth: 250,
  maxWidth: 250,
  flexGrow: 1,
  height: 'fit-content',
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'none',
};

export const TrendyolOrdersPage = () => {
  const navigate = useNavigate();

  const [orders, setOrders] = useState<PaginatedResponse<TrendyolOrder>>({
    data: [],
    totalCount: 0,
  });
  const [filter, setFilter] = useState(getDefaultPlatformOrderFilter());
  const [loading, setLoading] = useState(false);

  const refreshOrders = (filters?: PlatformOrderFilter) => {
    setLoading(true);
    getTrendyolOrders(filters ?? filter)
      .then((res) => setOrders(res))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refreshOrders();
  }, []);

  const handleSearch = () => {
    const newFilter: OrderFilter = {
      ...filter,
      pagination: { ...filter.pagination, page: 0 },
    };

    setFilter(newFilter);
    refreshOrders(newFilter);
  };

  const handlePagination = (p: Pagination) => {
    const newFilter = { ...filter, pagination: p };

    setFilter(newFilter);
    refreshOrders(newFilter);
  };

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      <Waiting open={loading} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          mb: 1.5,
        }}
      >
        <Typography variant='h5' sx={{ ...sharedStyles.pageTitle, m: 'auto' }}>
          Trendyol Siparişleri
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant='contained'
          color='primary'
          onClick={() => navigate('/sales/trendyol/scan')}
          startIcon={<QrCodeScannerIcon />}
          sx={{ ...sharedStyles.buttonText, m: 'auto' }}
        >
          Barkod Tara
        </Button>
      </Box>

      <Paper sx={{ mb: 1.5, p: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 1.5,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <FilterSearchBox
            label='Müşteri Adı'
            filters={filter}
            onChange={setFilter}
            fieldName='customerName'
            onEnter={handleSearch}
          />

          <FilterSearchBox
            label='Paket No'
            filters={filter}
            onChange={setFilter}
            fieldName='id'
            onEnter={handleSearch}
          />

          <Box sx={dateTimePickerStyle}>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              locale={trLocale}
            >
              <DateTimePicker
                label='Başlangıç'
                onChange={(newStartAt) =>
                  setFilter({ ...filter, startAt: newStartAt ?? undefined })
                }
                value={filter.startAt ?? null}
                inputFormat='d MMMM yyyy HH:mm'
                renderInput={(params) => (
                  <TextField {...params} size='small' fullWidth />
                )}
              />
            </LocalizationProvider>
          </Box>

          <Box sx={dateTimePickerStyle}>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              locale={trLocale}
            >
              <DateTimePicker
                label='Bitiş'
                onChange={(newEndAt) =>
                  setFilter({ ...filter, endAt: newEndAt ?? undefined })
                }
                value={filter.endAt ?? null}
                inputFormat='d MMMM yyyy HH:mm'
                renderInput={(params) => (
                  <TextField {...params} size='small' fullWidth />
                )}
              />
            </LocalizationProvider>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexGrow: 1,
              gap: 1.5,
              justifyContent: 'end',
            }}
          >
            <Button
              variant='contained'
              startIcon={<DeleteIcon />}
              color='error'
              onClick={() => setFilter({ pagination: filter.pagination })}
              sx={{ ...sharedStyles.buttonText }}
            >
              Filtreyi Sıfırla
            </Button>

            <Button
              variant='contained'
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              sx={{ ...sharedStyles.buttonText }}
            >
              Sipariş Ara
            </Button>
          </Box>
        </Box>
      </Paper>

      <TrendyolOrdersTable
        orders={orders.data}
        totalCount={orders.totalCount}
        pagination={filter.pagination}
        onPagination={handlePagination}
      />
    </Box>
  );
};
